/**
 * Implements a panel with buttons for manipulation
 */
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import { IPriceAlert } from '@kattana-io/common';
import { actionShowModal } from '../../../actions/modal';
import {
  actionCancelPriceAlert,
  actionDuplicatePriceAlert,
} from '../../../actions/price_alerts';
import { IStore } from '../../../reducers';
import { ModalTypes } from '../../../reducers/modal';

interface props {
  alert: IPriceAlert;
  filterEnabled: boolean;
  onFilter: (input: string) => void;
}

const useEditFlags = (alert: IPriceAlert) => {
  const canBeEdited = alert.status === 'WATCH';
  const paModalVisible = useSelector(
    (state: IStore) => state.modal.modalName === ModalTypes.priceAlertEdit
  );
  const paId = useSelector((state: IStore) => state.modal.props.alert?.id);
  const isBeingEdited = paModalVisible ? paId == alert.id : false;

  return {
    canBeEdited,
    isBeingEdited,
  };
};

/**
 * Cache function refs
 */
const usePriceAlertActions = (alert: IPriceAlert) => {
  const dispatch = useDispatch();

  const onDelete = useCallback(() => {
    dispatch(actionCancelPriceAlert(alert));
  }, [alert]);

  const onDuplicate = useCallback(() => {
    dispatch(actionDuplicatePriceAlert(alert));
  }, [alert]);

  const onPriceChange = useCallback(() => {
    dispatch(actionShowModal(ModalTypes.priceAlertEdit, { alert }));
  }, [alert]);

  const onShare = useCallback(() => {
    const ts = Number(new Date());
    window.open(
      `https://twitter.com/intent/tweet?text=https://api-staging.kattana.trade/integrations/twitter/price_alerts/${alert.uuid}/html?time=${ts}`
    );
  }, [alert]);

  return {
    onDelete,
    onDuplicate,
    onPriceChange,
    onShare,
  };
};

const PriceAlertsActions = ({ alert, filterEnabled, onFilter }: props) => {
  const { onDelete, onDuplicate, onPriceChange, onShare } =
    usePriceAlertActions(alert);

  const onFilterClicked = () => {
    if (filterEnabled) {
      onFilter('');
    } else {
      onFilter(`${alert.chain}/${alert.pair}`);
    }
  };

  const { isBeingEdited, canBeEdited } = useEditFlags(alert);

  return (
    <div className='actions'>
      {canBeEdited && (
        <div
          className={`action ${isBeingEdited ? 'action--active' : ''}`}
          onClick={onPriceChange}
          data-for={`price-alerts-actions-tooltip-${alert.uuid}`}
          data-tip='Edit price'
        >
          <div className='action__icon icon-edit-square' />
        </div>
      )}
      <div
        className='action'
        onClick={onShare}
        data-for={`price-alerts-actions-tooltip-${alert.uuid}`}
        data-tip='Share'
      >
        <div className='action__icon icon-share' />
      </div>
      <div
        className='action'
        onClick={onDuplicate}
        data-for={`price-alerts-actions-tooltip-${alert.uuid}`}
        data-tip='Duplicate'
      >
        <div className='action__icon icon-copy' />
      </div>
      <div
        className={`action ${filterEnabled ? 'action--active' : ''}`}
        onClick={onFilterClicked}
        data-for={`price-alerts-actions-tooltip-${alert.uuid}`}
        data-tip='Filter only this pair'
      >
        <div className='action__icon icon-filter' />
      </div>
      <div
        className='action'
        onClick={onDelete}
        data-for={`price-alerts-actions-tooltip-${alert.uuid}`}
        data-tip='Remove'
      >
        <div className='action__icon icon-trash' />
      </div>
      <ReactTooltip
        id={`price-alerts-actions-tooltip-${alert.uuid}`}
        place='left'
        effect='solid'
        overridePosition={({ left }) => {
          return {
            left: left,
            top: -2,
          };
        }}
        className='actions__tooltip text_tooltip'
      />
    </div>
  );
};

export default PriceAlertsActions;
